/**
 * MODULE: _shared/lib/frontend/api/searchApi.ts
 * PURPOSE: Search API client - Cross-entity global search via data-management-service
 * 
 * DEPENDENCIES:
 *  - axios: HTTP client
 *  - ./config: API_CONFIG, getAuthHeaders
 * 
 * PUBLIC API:
 *  - SearchResult: TypeScript interface for search results
 *  - searchApi.search(query, types, limit): Global search across entity types
 * 
 * SIDE EFFECTS:
 *  - HTTP requests to /api/v1/search endpoint
 *  - Reads JWT token from localStorage
 * 
 * AGENT SAFETY: RESTRICTED
 * FILE SIZE: 45 / 150 lines
 * 
 * See: /README.md#documentation | /ARCHITECTURE.md | /_shared/lib/frontend/api/README.md
 */

/**
 * Global Search Widget API Integration
 * Connects to data-management-service via API Gateway
 * Consolidated service (Jan 2026): task, contact, note, file, metric, notification, search
 */

import axios from 'axios';
import { API_CONFIG, getAuthHeaders } from './config';

const API_BASE = API_CONFIG.baseURL;

export interface SearchResult {
  id: string;
  type: 'task' | 'event' | 'contact' | 'note' | 'file' | 'metric';
  title: string;
  description?: string;
  metadata?: Record<string, any>;
  url?: string;
}

export interface SearchResponse {
  query: string;
  results: SearchResult[];
  total: number;
  by_type: Record<string, number>;
}

// Using centralized getAuthHeaders from config.ts

export const searchApi = {
  // Global search across all entities
  search: async (
    query: string,
    params?: {
      types?: string; // Comma-separated: "task,event,contact"
      limit?: number;
    }
  ): Promise<SearchResponse> => {
    const response = await axios.get(`${API_BASE}/api/v1/search`, {
      params: { q: query, ...params },
      headers: getAuthHeaders()
    });
    return response.data;
  }
};

